/**
 * Base Portal Module
 * Abstrakcyjna klasa bazowa dla wszystkich modułów portali
 */

import { db, auth } from '../../../lib/firebase';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { PortalModule, ExportPackage, UploadResult, ExportAction } from './PortalModule';
import { PortalConfig, FTPConfig, APIConfig } from './PortalConfig';
import { Offer } from '../../offerService';

export abstract class BasePortalModule implements PortalModule {
  abstract name: string;
  abstract config: PortalConfig;

  /**
   * Pobiera konfigurację portalu z Firebase
   */
  async getConfig(): Promise<PortalConfig> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Brak aktywnej sesji');
      }

      const configRef = doc(db, 'portal_configs', this.name);
      const configSnap = await getDoc(configRef);

      if (configSnap.exists()) {
        return configSnap.data() as PortalConfig;
      }

      // Zwróć domyślną konfigurację
      return this.getDefaultConfig();
    } catch (error: any) {
      console.error(`Błąd podczas pobierania konfiguracji portalu ${this.name}:`, error);
      return this.getDefaultConfig();
    }
  }

  /**
   * Zapisuje konfigurację portalu do Firebase
   */
  async saveConfig(config: Partial<PortalConfig>): Promise<void> {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error('Brak aktywnej sesji');
      }

      const configRef = doc(db, 'portal_configs', this.name);
      const currentConfig = await this.getConfig();

      await setDoc(configRef, {
        ...currentConfig,
        ...config,
        updated_at: new Date().toISOString(),
        office_id: currentUser.uid
      }, { merge: true });

      // Aktualizuj lokalną konfigurację
      this.config = await this.getConfig();
    } catch (error: any) {
      console.error(`Błąd podczas zapisywania konfiguracji portalu ${this.name}:`, error);
      throw error;
    }
  }

  /**
   * Zwraca ID oferty do użycia (stare lub nowe)
   */
  getPortalOfferId(offer: Offer): string {
    const portalId = offer.portal_ids?.[this.name as keyof typeof offer.portal_ids];
    
    if (portalId) {
      // Oferta już ma ID na tym portalu - użyj go
      return portalId;
    }
    
    // Oferta nie ma ID na tym portalu - użyj external_id jeśli istnieje, w przeciwnym razie id
    return offer.external_id || offer.id;
  }

  /**
   * Sprawdza czy oferta już istnieje na portalu
   * Domyślna implementacja - sprawdza czy portal_id istnieje
   */
  async checkOfferExists(portalId: string): Promise<boolean> {
    // Domyślna implementacja - w większości przypadków nie możemy sprawdzić
    // czy oferta istnieje bez wywołania API portalu
    // To powinno być nadpisane w konkretnych implementacjach
    return portalId !== undefined && portalId !== '';
  }

  /**
   * Waliduje czy użytkownik może edytować ofertę
   */
  async validateOfferOwnership(offer: Offer, userId: string): Promise<boolean> {
    // Sprawdź czy użytkownik jest właścicielem oferty
    if (offer.userId === userId) {
      return true;
    }

    // Sprawdź czy użytkownik jest adminem
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      const userData = userDoc.data();
      if (userData?.role === 'admin') {
        return true;
      }
    } catch (error) {
      console.error('Błąd podczas sprawdzania roli użytkownika:', error);
    }

    return false;
  }

  /**
   * Sprawdza czy edycja jest aktywna dla tego portalu
   */
  async canEdit(): Promise<boolean> {
    const config = await this.getConfig();
    return config.edit_active === true;
  }

  /**
   * Sprawdza czy usuwanie jest aktywne dla tego portalu
   */
  async canDelete(): Promise<boolean> {
    const config = await this.getConfig();
    return config.delete_active === true;
  }

  /**
   * Zwraca domyślną konfigurację portalu
   */
  protected abstract getDefaultConfig(): PortalConfig;

  /**
   * Abstrakcyjne metody do zaimplementowania w konkretnych portalach
   */
  abstract generateExportPackage(offer: Offer, action: ExportAction): Promise<ExportPackage>;
  abstract uploadPackage(exportPackage: ExportPackage): Promise<UploadResult>;
  abstract testConnection(): Promise<{ success: boolean; message: string }>;
}

